/**
 * Ползунок даты единой карты (agroai-6al.1).
 *
 * Карта сама данные слоя не перезапрашивает: ползунок отдаёт странице новую
 * дату (`as_of`) колбэком, страница перечитывает значения слоя и передаёт их
 * обратно пропсами. Геометрия при этом не меняется.
 *
 * Дата отдаётся по отпусканию (и с задержкой при клавиатуре), а не на каждом
 * шаге перетаскивания: каждый шаг — это запрос среза.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { CalendarDays } from 'lucide-react';
import { Icon } from '../ui/Icon';
import { dateShort } from '../lib/format';
import { defineMessages } from '../../i18n/define';
import { useT } from '../../i18n/hooks';

const m = defineMessages({
  dataOn: {
    id: 'map.timeline.dataOn',
    defaultMessage: 'Данные на',
    description: 'Подпись ползунка даты под картой: данные слоя на выбранную дату (после неё стоит дата)',
  },
  today: {
    id: 'map.timeline.today',
    defaultMessage: 'сегодня',
    description: 'Пометка при последней дате шкалы; коротко',
  },
  rangeLabel: {
    id: 'map.timeline.rangeLabel',
    defaultMessage: '{label}: дата данных слоя',
    description: 'aria-label ползунка даты на карте; {label} — подпись ползунка',
  },
  reset: {
    id: 'map.timeline.reset',
    defaultMessage: 'Сегодня',
    description: 'Кнопка возврата ползунка на последнюю дату; коротко',
  },
  latest: {
    id: 'map.timeline.latest',
    defaultMessage: '{date} · {today}',
    description: 'Выбранная дата с пометкой «сегодня»; {date} — дата, {today} — «сегодня»',
  },
});


export interface MapTimelineProps {
  /** Первая дата шкалы, ISO `YYYY-MM-DD`. */
  from: string;
  /** Последняя дата шкалы, ISO; обычно «сегодня» среза. */
  to: string;
  /** Текущая дата данных; `null` — данные «на сегодня» (последняя точка). */
  value: string | null;
  onChange: (asOf: string | null) => void;
  step?: 'day' | 'week';
  /** Подпись перед датой; по умолчанию «Данные на». */
  label?: string;
}

const DAY = 86_400_000;

function parse(iso: string): number {
  return Date.parse(`${iso}T00:00:00Z`);
}

function iso(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

/**
 * Точки шкалы: от `from` шагом до `to`; последняя точка — всегда `to`, даже
 * если шаг до неё не ровный (иначе «сегодня» нельзя было бы выбрать).
 */
export function timelineDates(from: string, to: string, step: 'day' | 'week' = 'week'): string[] {
  const start = parse(from);
  const end = parse(to);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return [to];
  const stride = step === 'day' ? DAY : 7 * DAY;
  const out: string[] = [];
  for (let t = start; t < end; t += stride) out.push(iso(t));
  out.push(iso(end));
  return out;
}

/** Ближайшая точка шкалы к дате (дата вне шкалы прижимается к краю). */
export function nearestIndex(dates: readonly string[], value: string | null): number {
  if (value === null || dates.length === 0) return Math.max(0, dates.length - 1);
  const target = parse(value);
  let best = 0;
  for (let i = 1; i < dates.length; i += 1) {
    if (Math.abs(parse(dates[i]!) - target) < Math.abs(parse(dates[best]!) - target)) best = i;
  }
  return best;
}

export function MapTimeline({ from, to, value, onChange, step = 'week', label: labelProp }: MapTimelineProps) {
  const t = useT();
  const label = labelProp ?? t(m.dataOn);
  const dates = useMemo(() => timelineDates(from, to, step), [from, to, step]);
  const committed = nearestIndex(dates, value);
  const [draft, setDraft] = useState(committed);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => setDraft(committed), [committed]);
  useEffect(() => () => window.clearTimeout(timer.current), []);

  const commit = (index: number) => {
    window.clearTimeout(timer.current);
    if (index === committed) return;
    onChange(index === dates.length - 1 ? null : dates[index]!);
  };

  const shown = dates[draft] ?? to;
  const isLatest = draft === dates.length - 1;

  return (
    <div className="maptimeline">
      <span className="maptimeline__label">
        <Icon icon={CalendarDays} size={16} />
        {label}
      </span>
      <strong className="maptimeline__date num" aria-live="polite">
        {isLatest ? t(m.latest, { date: dateShort(shown), today: t(m.today) }) : dateShort(shown)}
      </strong>
      <input
        className="maptimeline__range focus-ring"
        type="range"
        min={0}
        max={Math.max(0, dates.length - 1)}
        step={1}
        value={draft}
        aria-label={t(m.rangeLabel, { label })}
        aria-valuetext={dateShort(shown)}
        onChange={(event) => {
          const next = Number(event.target.value);
          setDraft(next);
          // Клавиатура шагает без «отпускания»: отдаём дату после паузы.
          window.clearTimeout(timer.current);
          timer.current = window.setTimeout(() => commit(next), 450);
        }}
        onPointerUp={(event) => commit(Number((event.target as HTMLInputElement).value))}
      />
      <span className="maptimeline__edge num">{dateShort(dates[0])}</span>
      {!isLatest ? (
        <button type="button" className="maptimeline__reset focus-ring" onClick={() => commit(dates.length - 1)}>
          {t(m.reset)}
        </button>
      ) : null}
    </div>
  );
}
